import {
  cloneElement,
  useCallback,
  ReactNode,
  useEffect,
  Children,
  useState,
  useRef,
  memo,
} from 'react';

import ResizeObserver from 'resize-observer-polyfill';
import { BreakPoints } from '@picsart/rc/enums';

import useMasonryGridVerticalStyles from './MasonryGridVarticalJSS';

type MasonryGridVerticalProps = {
  children: ReactNode | any;

  isSSR?: boolean;

  isDynamic?: boolean;

  XL_columnsCount: number;
  XL_columnSize: number;
  XL_gridVerticalGutter?: number;
  XL_gridHorizontalGutter?: number;

  LG_columnsCount: number;
  LG_columnSize: number;
  LG_gridVerticalGutter?: number;
  LG_gridHorizontalGutter?: number;

  MD_columnsCount: number;
  MD_columnSize: number;
  MD_gridVerticalGutter?: number;
  MD_gridHorizontalGutter?: number;

  SM_columnsCount: number;
  SM_columnSize: number;
  SM_gridVerticalGutter?: number;
  SM_gridHorizontalGutter?: number;
};

function MasonryGridVertical(props: MasonryGridVerticalProps) {
  const {
    children,

    isSSR = false,

    isDynamic = true,

    XL_columnsCount,
    XL_columnSize,
    XL_gridVerticalGutter,
    XL_gridHorizontalGutter,

    LG_columnsCount,
    LG_columnSize,
    LG_gridVerticalGutter,
    LG_gridHorizontalGutter,

    MD_columnsCount,
    MD_columnSize,
    MD_gridVerticalGutter,
    MD_gridHorizontalGutter,

    SM_columnsCount,
    SM_columnSize,
    SM_gridVerticalGutter,
    SM_gridHorizontalGutter,
  } = props;

  const masonryGridVerticalStylesClassNames = useMasonryGridVerticalStyles({
    isSSR,
    isDynamic,
    XL_columnsCount,
    XL_columnSize,
    XL_gridVerticalGutter,
    XL_gridHorizontalGutter,

    LG_columnsCount,
    LG_columnSize,
    LG_gridVerticalGutter,
    LG_gridHorizontalGutter,

    MD_columnsCount,
    MD_columnSize,
    MD_gridVerticalGutter,
    MD_gridHorizontalGutter,

    SM_columnsCount,
    SM_columnSize,
    SM_gridVerticalGutter,
    SM_gridHorizontalGutter,
  } as any);

  const childProps = {
    isDynamic,
    XL_columnsCount,
    XL_columnSize,
    XL_gridVerticalGutter,
    XL_gridHorizontalGutter,

    LG_columnsCount,
    LG_columnSize,
    LG_gridVerticalGutter,
    LG_gridHorizontalGutter,

    MD_columnsCount,
    MD_columnSize,
    MD_gridVerticalGutter,
    MD_gridHorizontalGutter,

    SM_columnsCount,
    SM_columnSize,
    SM_gridVerticalGutter,
    SM_gridHorizontalGutter,
  };

  const container = useRef<HTMLDivElement>(null);

  const [style, setStyle] = useState<{ [key: string]: any }>({});

  const calcWSize = useCallback<() => string>(() => {
    const width = window.innerWidth;
    let size = 'XL';
    if (width <= BreakPoints.Small) {
      size = 'SM';
    } else if (width <= BreakPoints.Medium) {
      size = 'MD';
    } else if (width <= BreakPoints.Large) {
      size = 'LG';
    }

    return size;
  }, []);

  const calc = useCallback(
    (height = 0) => {
      const maxGutter =
        Math.max(
          XL_gridVerticalGutter || 0,
          LG_gridVerticalGutter || 0,
          MD_gridVerticalGutter || 0,
          SM_gridVerticalGutter || 0,
        ) * 2;

      if (height) {
        setStyle({ maxHeight: `${Math.ceil(height) + maxGutter}px` });
      }
      if (container.current) {
        const colCount = props[`${calcWSize()}_columnsCount`];
        const colHeights = Array(colCount).fill(0);

        Array.from(container.current.children).forEach(
          (child: any, i: number) => {
            const order = i % colCount;
            child.style.order = order;
            colHeights[order] += parseFloat(child.clientHeight);
          },
        );

        setStyle({
          maxHeight: `${Math.ceil(Math.max(...colHeights) + maxGutter)}px`,
        });
      }
    },
    [calcWSize, props],
  );

  useEffect(() => {
    const observer = new ResizeObserver((entries: any) => {
      window.requestAnimationFrame(() => {
        if (!Array.isArray(entries) || !entries.length) {
          return;
        }
        const rect = entries[0].contentRect;
        const { height } = rect;
        calc(height);
      });
    });

    const element = container.current;

    observer.observe(element);

    calc();

    return () => {
      observer.unobserve(element);
    };
  }, [children, calc]);

  return (
    <div className={masonryGridVerticalStylesClassNames.root}>
      <div className={masonryGridVerticalStylesClassNames.grid}>
        <div
          className={masonryGridVerticalStylesClassNames.row}
          ref={container}
          style={style}
        >
          {Children.map(children, child =>
            cloneElement(child, childProps, child.props.children),
          )}
        </div>
      </div>
    </div>
  );
}

export default memo<MasonryGridVerticalProps>(MasonryGridVertical);
